// imageToEpos.ts

export type MonoDitherMode = 'ordered' | 'errorDiffusion';

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/**
 * Encode a binary string (charCode 0–255) to Base64.
 * This mimics the original toBase64Binary but with modern TS.
 */
export function toBase64Binary(binary: string): string {
  const length = binary.length;
  const paddingBytes = (3 - (length % 3)) % 3; // 0,1,2

  const result: string[] = [];
  let i = 0;

  // Pad with two zero bytes so we can always read 3 bytes
  const padded = binary + '\x00\x00';

  while (i < length) {
    const b1 = padded.charCodeAt(i++);
    const b2 = padded.charCodeAt(i++);
    const b3 = padded.charCodeAt(i++);

    const n = (b1 << 16) | (b2 << 8) | b3;

    result.push(
      BASE64_ALPHABET[(n >> 18) & 0x3f],
      BASE64_ALPHABET[(n >> 12) & 0x3f],
      BASE64_ALPHABET[(n >> 6) & 0x3f],
      BASE64_ALPHABET[n & 0x3f],
    );
  }

  // Replace last 1–2 chars with '=' depending on paddingBytes
  for (let k = 0; k < paddingBytes; k++) {
    result[result.length - 1 - k] = '=';
  }

  return result.join('');
}

/**
 * Convert ImageData (RGBA) to 1-bit monochrome bitmap string for ePOS.
 *
 * Each character in the returned string is one byte (8 pixels),
 * MSB = leftmost pixel.
 *
 * mode:
 *   - 'ordered' → 8x8 ordered dithering
 *   - 'errorDiffusion' → Floyd–Steinberg-ish
 */
export function toMonoImage(
  imgData: ImageData,
  options: { mode?: MonoDitherMode; gamma?: number } = {},
): string {
  const { mode = 'ordered', gamma = 1.8 } = options;
  const { data, width: w, height: h } = imgData;

  const bytesPerRow = (w + 7) >> 3; // ceil(w / 8)
  const out: string[] = new Array(bytesPerRow * h);

  // 8x8 threshold matrix
  const thresholdMatrix8: number[][] = [
    [2, 130, 34, 162, 10, 138, 42, 170],
    [194, 66, 226, 98, 202, 74, 234, 106],
    [50, 178, 18, 146, 58, 186, 26, 154],
    [242, 114, 210, 82, 250, 122, 218, 90],
    [14, 142, 46, 174, 6, 134, 38, 166],
    [206, 78, 238, 110, 198, 70, 230, 102],
    [62, 190, 30, 158, 54, 182, 22, 150],
    [254, 126, 222, 94, 246, 118, 214, 86],
  ];

  const error: number[] = [];
  if (mode === 'errorDiffusion') {
    for (let i = 0; i < w; i++) {
      error[i] = 0;
    }
  }

  const charFromCode = String.fromCharCode;

  let srcIndex = 0;
  let outIndex = 0;

  for (let y = 0; y < h; y++) {
    let byteValue = 0;
    let errorLeft = 0;
    let errorDiag = 0;

    for (let x = 0; x < w; x++) {
      const bitPos = x & 7;

      let threshold = 128;
      if (mode === 'ordered') {
        threshold = thresholdMatrix8[y & 7][bitPos];
      }

      // RGBA → grayscale 0..1 with alpha on white
      const r = data[srcIndex++];
      const g = data[srcIndex++];
      const b = data[srcIndex++];
      const a = data[srcIndex++];

      const luma = r * 0.29891 + g * 0.58661 + b * 0.11448;
      const gray0to1 = ((luma * a) / 255 + 255 - a) / 255;

      let v = Math.pow(gray0to1, 1 / gamma) * 255;
      v |= 0; // int

      if (mode === 'errorDiffusion') {
        v += (error[x] + errorLeft) >> 4;

        const outputLevel = v < threshold ? 0 : 255;
        const diff = v - outputLevel;

        if (x > 0) {
          error[x - 1] += diff; // left
        }
        error[x] = diff * 7 + errorDiag; // below
        errorLeft = diff * 5; // current
        errorDiag = diff * 3; // below-right
      }

      const isBlack = v < threshold;
      if (isBlack) {
        byteValue |= 0x80 >> bitPos;
      }

      const atByteBoundary = bitPos === 7 || x === w - 1;
      if (atByteBoundary) {
        // Avoid raw 0x10 (DLE) because of printer protocols
        const safeByte = byteValue === 0x10 ? 0x20 : byteValue;
        out[outIndex++] = charFromCode(safeByte);
        byteValue = 0;
      }
    }
  }

  return out.join('');
}

/**
 * Convert ImageData (RGBA) to 4-bit grayscale bitmap string (2 pixels per byte).
 *
 * Each character in the returned string is one byte:
 *   high nibble = left pixel, low nibble = right pixel.
 */
export function toGrayImage(imgData: ImageData, options: { gamma?: number } = {}): string {
  const { gamma = 1.8 } = options;
  const { data, width: w, height: h } = imgData;

  const bytesPerRow = (w + 1) >> 1; // ceil(w / 2)
  const out: string[] = new Array(bytesPerRow * h);
  const charFromCode = String.fromCharCode;

  // 4x4 ordered dithering matrix
  const matrix4: number[][] = [
    [0, 9, 2, 11],
    [13, 4, 15, 6],
    [3, 12, 1, 10],
    [16, 7, 14, 5],
  ];

  // Thermal compensation LUT
  const thermal: number[] = [
    0,
    7,
    13,
    19,
    23,
    27,
    31,
    35,
    40,
    44,
    49,
    52,
    54,
    55,
    57,
    59,
    61,
    62,
    64,
    66,
    67,
    69,
    70,
    70,
    71,
    72,
    73,
    74,
    75,
    76,
    77,
    78,
    79,
    80,
    81,
    82,
    83,
    83,
    84,
    85,
    86,
    86,
    87,
    88,
    88,
    89,
    90,
    90,
    91,
    91,
    92,
    93,
    93,
    94,
    94,
    95,
    96,
    96,
    97,
    98,
    98,
    99,
    99,
    100,
    101,
    101,
    102,
    102,
    103,
    103,
    104,
    104,
    105,
    105,
    106,
    106,
    107,
    107,
    108,
    108,
    109,
    109,
    110,
    110,
    111,
    111,
    112,
    112,
    112,
    113,
    113,
    114,
    114,
    115,
    115,
    116,
    116,
    117,
    117,
    118,
    118,
    119,
    119,
    120,
    120,
    120,
    121,
    121,
    122,
    122,
    123,
    123,
    123,
    124,
    124,
    125,
    125,
    125,
    126,
    126,
    127,
    127,
    127,
    128,
    128,
    129,
    129,
    130,
    130,
    130,
    131,
    131,
    132,
    132,
    132,
    133,
    133,
    134,
    134,
    135,
    135,
    135,
    136,
    136,
    137,
    137,
    137,
    138,
    138,
    139,
    139,
    139,
    140,
    140,
    141,
    141,
    141,
    142,
    142,
    143,
    143,
    143,
    144,
    144,
    145,
    145,
    146,
    146,
    146,
    147,
    147,
    148,
    148,
    148,
    149,
    149,
    150,
    150,
    150,
    151,
    151,
    152,
    152,
    152,
    153,
    153,
    154,
    154,
    155,
    155,
    155,
    156,
    156,
    157,
    157,
    158,
    158,
    159,
    159,
    160,
    160,
    161,
    161,
    161,
    162,
    162,
    163,
    163,
    164,
    164,
    165,
    165,
    166,
    166,
    166,
    167,
    167,
    168,
    168,
    169,
    169,
    170,
    170,
    171,
    171,
    172,
    173,
    173,
    174,
    175,
    175,
    176,
    177,
    178,
    178,
    179,
    180,
    180,
    181,
    182,
    182,
    183,
    184,
    184,
    185,
    186,
    186,
    187,
    189,
    191,
    193,
    195,
    198,
    200,
    202,
    255,
  ];

  let srcIndex = 0;
  let outIndex = 0;

  for (let y = 0; y < h; y++) {
    let packedByte = 0;

    for (let x = 0; x < w; x++) {
      const isRightPixel = (x & 1) === 1;

      const r = data[srcIndex++];
      const g = data[srcIndex++];
      const b = data[srcIndex++];
      const a = data[srcIndex++];

      const luma = r * 0.29891 + g * 0.58661 + b * 0.11448;
      const gray0to1 = ((luma * a) / 255 + 255 - a) / 255;

      let v = Math.pow(gray0to1, 1 / gamma) * 255;
      v |= 0;
      v = thermal[v]; // thermal-compensated 0..255

      let level = (v / 17) | 0; // 0..15 roughly
      const remainder = v % 17;
      const threshold = matrix4[y & 3][x & 3];

      if (threshold < remainder) {
        level++;
      }

      // Pack into high/low nibble
      if (!isRightPixel) {
        // left pixel
        packedByte = (level & 0x0f) << 4;
      } else {
        // right pixel → flush byte
        packedByte |= level & 0x0f;
        out[outIndex++] = charFromCode(packedByte);
        packedByte = 0;
      }

      // If width is odd, flush leftover nibble
      if (x === w - 1 && !isRightPixel) {
        out[outIndex++] = charFromCode(packedByte);
        packedByte = 0;
      }
    }
  }

  return out.join('');
}

export const createEpsonImage = async (base64Image: string, logger: any, paperWidth: number) => {
  // Create an image element to load the base64 data
  const img = new Image();

  // Wait for image to load
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = (error) => {
      logger.error('Failed to load base64 image', { errorMessage: String(error) });
      reject(error);
    };
    // Set the base64 image source
    img.src = base64Image!.startsWith('data:')
      ? base64Image!
      : `data:image/png;base64,${base64Image}`;
  });

  // Calculate scaled dimensions to fit within paperWidth while maintaining aspect ratio
  let targetWidth = img.width;
  let targetHeight = img.height;

  if (img.width > paperWidth) {
    const scaleFactor = paperWidth / img.width;
    targetWidth = paperWidth;
    targetHeight = Math.round(img.height * scaleFactor);
  }

  // Create canvas with scaled dimensions
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas 2D context');
  }

  // Draw the image onto the canvas at the scaled size
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  // Get image data from canvas
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  logger.info('Image loaded and converted to canvas', {
    width: imgData.width,
    height: imgData.height,
  });

  // Convert to monochrome binary format for Epson
  const monoBinary = toMonoImage(imgData, {
    mode: 'ordered',
    gamma: 1.4,
  });

  // Encode to base64
  const monoB64 = toBase64Binary(monoBinary);

  return {
    data: monoB64,
    width: imgData.width,
    height: imgData.height,
  };
};
